import React, { useEffect, useState } from 'react'
import Tooltip from 'rc-tooltip'
import 'rc-tooltip/assets/bootstrap_white.css'
import { Card } from '../../../components/Card'
import { CloseBtn } from '../../../components/CloseBtn'
import { ModalProvider } from '../../../helpers/ModalContext'
import FormattedMessage from '../../../helpers/FormattedMessage'
import { unionCloth, printColors, logoFonts } from '../../../setups/setups'
import { useModal } from '../../../components/Modal'
import { FileInput } from '../../../components/FileInput'
import { TopSection } from '../../../components/TopSection'
import { Prompt } from '../../../components/Prompt'
import { Select } from '../../../components/Select'
import { logoStaticContent, designOptionsStaticContent } from '../../../components/ModalStatic'
import { Button } from 'src/components/Button'
import { InputButton } from 'src/components/InputButton'
import { LocalCard } from '../Step4/LocalCard'
import { CustomInput } from '../../../components/CustomInput'
import styles from './EditPage.module.scss'
import { sendFiles } from '../../../helpers/sendFiles'

interface IProps {
  setPage: (number: number) => void
}

export type CardType = {
  clothType: string
  color: string
  design: string
  logos: {
    url: string
    name: string
  }[]
  persons: {
    size: string,
    firstText: string
    secondText: string
  }[]
  otherLogoColor: boolean
  logoColor: string
  logoFont: string
}

export const EditPage = ({ setPage }: IProps) => {
  const index = window.localStorage.getItem('Union-editItem') || -1
  const item = JSON.parse(window.localStorage.getItem('Union-savedUnionItems') || '[]')
  const data = item[index]

  const { Modal, openModal, closeModal } = useModal()
  const [color, setColor] = useState(data.color)
  const [design, setDesign] = useState(data.design)
  const [logoColor, setLogoColor] = useState(data.logoColor)
  const [logoFiles, setLogoFiles] = useState(data.logos)
  const [logoFont, setLogoFont] = useState(data.logoFont)
  const [cancelPrompt, setCancelPrompt] = useState(false)
  const [withoutLogo, setWithoutLogo] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [persons, setPersons] = useState(data.persons)
  const [size, setSize] = useState('')
  const [firstText, setFirstText] = useState('')
  const [secondText, setSecondText] = useState('')
  const clothType = data.clothType
  const [filesChanged, setFilesChanged] = useState(false)
  const [openContent, setOpenContent] = useState(-1)
  const [otherLogoColor, setOtherLogoColor] = useState(data.otherLogoColor)
  const [removedFilesOnly, setRemovedFilesOnly] = useState(false)

  useEffect(() => {
    window.localStorage.setItem('Union-step', '6')
  }, [])

  const saveData = (urls: string[], removedFilesOnly?: boolean) => {
    const item: CardType = {
      color,
      clothType,
      design,
      logoColor,
      logoFont,
      persons,
      otherLogoColor,
      logos: filesChanged && !removedFilesOnly ? urls.map((item, index) => {
        return {
          url: item,
          name: logoFiles[index].path
        }
      }) : logoFiles
    }

    const savedItems = JSON.parse(window.localStorage.getItem('Union-savedUnionItems') || '[]')
    savedItems[index] = item
    window.localStorage.setItem('Union-savedUnionItems', JSON.stringify(savedItems))
    setPage(5)
  }

  const onRemove = () => {
    setRemovedFilesOnly(true)
  }

  const setFontValue = (font: string) => {
    setIsError(false)
    setLogoFont(font)
    closeModal()
  }

  const setDesignValue = (design: string) => {
    setDesign(design)
    closeModal()
  }

  const setLogoColorValue = (color: string) => {
    setIsError(false)
    setLogoColor(color)
  }

  const resetPromptCancel = () => {
    setCancelPrompt(false)
  }

  const resetPromptSubmit = () => {
    setPage(5)
  }

  const openResetPrompt = () => {
    setCancelPrompt(true)
  }

  const logoPromptCancel = () => {
    setWithoutLogo(false)
  }

  const logoPromptSubmit = () => {
    setWithoutLogo(false)
    setOpenContent(state => state + 1)
  }

  const onLogoChange = (files: Array<any>) => {
    setFilesChanged(true)
    if (files.length < 1) {
      setRemovedFilesOnly(true)
    } else {
      setRemovedFilesOnly(false)
    }
    setLogoFiles(files)
  }

  const setOtherLogoColorValue = () => {
    setOtherLogoColor(true)
    setLogoColor('')
  }

  const setSizeValue = (size: string | number) => {
    setIsError(false)
    setSize(`${size}`)
  }

  const setFirstTextValue = (firstText: string) => {
    setIsError(false)
    setFirstText(firstText)
  }

  const setSecondTextValue = (secondText: string) => {
    setIsError(false)
    setSecondText(secondText)
  }

  const addItems = () => {
    if (size === '' || firstText === '' || secondText === '') {
      setIsError(true)
    } else {
      const newPersons: {
        size: string
        firstText: string
        secondText: string
      }[] = persons
      newPersons.push({
        size,
        firstText,
        secondText,
      })
      setSize('')
      setFirstText('')
      setSecondText('')
    }
  }

  const next = () => {
    // if ((logoFiles.length < 1 || logoFiles.length > 1) && window.localStorage.getItem('Union-noLogoPrompt') !== 'hideLogo') {
    //   setOpenContent(2)
    // } else
    if(logoColor === '') {
      setIsError(true)
      setOpenContent(3)
    } else if (!persons.length) {
      setOpenContent(5)
      setIsError(true)
    } else if (removedFilesOnly) {
      saveData([], removedFilesOnly)
    } else {
      sendFiles('union', logoFiles, saveData, setIsLoading, filesChanged)
    }
  }

  const designModal = (
    <div className={styles.Options}>
      {Object.keys(unionCloth[clothType].designTypes).map((item) => (
        <div className={`${styles.OptionWrapper} ${design === item && styles.Active}`} key={item} onClick={() => {
          setDesignValue(item)
        }}>
          <img src={unionCloth[clothType].designTypes[item].img} alt={item}/>
          <div>
          <FormattedMessage id={`Design.${unionCloth[clothType].designTypes[item].title}`}/>
          </div>
        </div>
      ))}
    </div>
  )

  const logoFontModal = (
    <div className={styles.Options}>
      {Object.keys(logoFonts).map((item) => (
        <div className={`${styles.OptionWrapper} ${logoFont === item && styles.Active}`} key={item} onClick={() => {
          setFontValue(item)
        }}>
          <img src={logoFonts[item].img} alt={item}/>
          <div>
            <FormattedMessage id={`LogoFont.${item}`}/>
          </div>
        </div>
      ))}
    </div>
  )

  const logoColorModalContent = (
    <div className="static-wrapper">
      <div className={styles.LogoItemsWrapper}>
        {Object.keys(printColors).map((itemColor) => (
          <div key={itemColor}>
            <div
              className={styles.LogoColor}
              style={{
                backgroundColor: unionCloth[clothType].colors[color],
                color: printColors[itemColor],
              }}
            >
              A
            </div>
            <div className={styles.LogoColorName}>
              <FormattedMessage id={`Color.${itemColor}`}/>
            </div>
          </div>
        ))}
      </div>
      <div className="info-block">
        <h3><FormattedMessage id="LogoColorModal.info.title" /></h3>
        <p><FormattedMessage id="LogoColorModal.info.text" /></p>
        <a href="https://pantone-colours.com/" target="_blank" rel="noreferrer">https://wwww.pantone-colours.com/</a>
      </div>
    </div>
  )

  return (
    <ModalProvider>
      <TopSection
        title="SchoolShop.topSection.create.title"
        description="SchoolShop.topSection.create.description"
        hideMobile
      />
      <div className={styles.Wrapper}>
        <CloseBtn cancel={openResetPrompt}/>
        <Card
          title={'SchoolShop.stage3.title'}
          description={'SchoolShop.stage3.description'}
          value={`Color.${color}`}
          onContentOpen={() => {
            setOpenContent(0)
          }}
          hideContent={openContent !== 0}
        >
          <div className={styles.SelectColorWrapper}>
            <h2><FormattedMessage id={'SchoolShop.stage3.SelectColor'}/></h2>
            <div><FormattedMessage id={`Color.${color}`}/></div>
          </div>
          <div className={styles.ColorsWrapper}>
            {Object.keys(unionCloth[clothType].colors).map((itemColor) => (
              <Tooltip
                placement="bottom"
                overlayInnerStyle={{
                  padding: '16px',
                  marginTop: '-5px',
                  color: '#3D3E43',
                  fontSize: '14px',
                  border: '1px solid #E9EAED',
                  borderRadius: '4px'
                }}
                overlay={<FormattedMessage id={`Color.${itemColor}`}/>}
                key={itemColor}
              >
                <div className={`${styles.ColorContainer} ${color === itemColor && styles.Active}`}>
                  <div
                    className={styles.Color}
                    style={{ backgroundColor: unionCloth[clothType].colors[itemColor] }}
                    onClick={() => {
                      setColor(itemColor)
                    }}
                  />
                </div>
              </Tooltip>
            ))}
          </div>
        </Card>
        <Card
          title={'SchoolShop.stage4.design.title'}
          description={'SchoolShop.stage4.design.description'}
          value={`Design.${design}`}
          discoverMore={designOptionsStaticContent}
          discoverMoreTitle="SchoolShop.stage4.design.modal.discoverMore.title"
          onContentOpen={() => {
            setOpenContent(1)
          }}
          hideContent={openContent !== 1}
        >
          <InputButton
            label="SchoolShop.stage4.modal.design.title"
            onClick={openModal}
            errorLabel="SchoolShop.stage4.design.error"
          >
            {<FormattedMessage id={design ? `Design.${design}` : 'Design.placeholder'}/>}
          </InputButton>
        </Card>
        <Card
          title={'SchoolShop.stage4.logo.title'}
          warningMessage={'Overview.props.noLogo'}
          description={'SchoolShop.stage4.logo.description'}
          value={logoFiles.map((file: any) => file.name).join(', ')}
          discoverMore={logoStaticContent}
          discoverMoreTitle="SchoolShop.stage4.logo.modal.title"
          onContentOpen={() => {
            setOpenContent(2)
          }}
          hideContent={openContent !== 2}
        >
          <FileInput
            design={design}
            union={true}
            valueFiles={logoFiles}
            onChange={onLogoChange}
            onRemove={onRemove}
          />
        </Card>
        {withoutLogo &&
        <Prompt
            title="SchoolShop.stage4.logoPrompt.title"
            text="SchoolShop.stage4.logoPrompt.text"
            cancel={logoPromptCancel}
            submit={logoPromptSubmit}
            submitText="continue"
            warning
        />
        }
        {cancelPrompt &&
        <Prompt
            title="SchoolShop.stage4.resetPrompt.title"
            text="SchoolShop.stage4.resetPrompt.text"
            cancel={resetPromptCancel}
            submit={resetPromptSubmit}
            submitText="continue"
        />
        }
        <Card
          title={'SchoolShop.stage4.logoColor.title'}
          description={'SchoolShop.stage4.logoColor.description'}
          value={otherLogoColor ? logoColor : logoColor && `Color.${logoColor}`}
          discoverMore={logoColorModalContent}
          discoverMoreTitle="SchoolShop.stage4.logoColor.modal.title"
          onContentOpen={() => {
            setOpenContent(3)
          }}
          hideContent={openContent !== 3}
        >
          <div className={styles.SelectColorWrapper}>
            <h2><FormattedMessage id={'SchoolShop.stage3.SelectLogoColor'} /></h2>
            {(logoColor || otherLogoColor) && <div><FormattedMessage id={`Color.${otherLogoColor ? "special.pantone" : logoColor}`}/></div>}
          </div>
          <div className={styles.ColorsWrapper}>
            {Object.keys(printColors).map((itemColor) => (
              <Tooltip
                placement="bottom"
                overlayInnerStyle={{
                  padding: '16px',
                  marginTop: '-5px',
                  color: '#3D3E43',
                  fontSize: '14px',
                  border: '1px solid #E9EAED',
                  borderRadius: '4px'
                }}
                overlay={<FormattedMessage id={`Color.${itemColor}`} />}
                key={itemColor}
              >
                <div
                  className={`${styles.ColorContainer} ${logoColor === itemColor && styles.Active} ${isError && styles.Error}`}>
                  <div
                    className={styles.Color}
                    style={
                      { background: printColors[itemColor] }}
                    onClick={() => {
                      setOtherLogoColor(false)
                      setLogoColorValue(itemColor)
                    }}
                  />
                </div>
              </Tooltip>
            ))}
            <Tooltip
              placement="bottom"
              overlayInnerStyle={{
                padding: '16px',
                marginTop: '-5px',
                color: '#3D3E43',
                fontSize: '14px',
                border: '1px solid #E9EAED',
                borderRadius: '4px'
              }}
              overlay={<FormattedMessage id={"Color.special.pantone"} />}
              key={"Color.other"}
            >
              <div className={`${styles.ColorContainer} ${otherLogoColor && styles.Active} ${isError && styles.Error}`}>
                <div
                  className={styles.Color}
                  style={{ textAlign: 'center',
                    fontSize: '36px',
                    lineHeight: '20px',
                    letterSpacing: '.5px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setOtherLogoColorValue()
                  }}
                >...
                </div>
              </div>
            </Tooltip>
          </div>

          {otherLogoColor &&
          <>
              <CustomInput
                  title={"Color.special.pantone"}
                  placeholder={"For example: PANTONE 185 C"}
                  value={logoColor}
                  type={"text"}
                  onChange={setLogoColorValue}
              />
              <a className={styles.Link} href="http://www.pantone-colours.com/" target="_blank" rel="noreferrer">
                  <FormattedMessage id={"Color.customInput.link"} />
              </a>
          </>
          }
          {
            otherLogoColor
              ? <div className={styles.OtherDescription}><FormattedMessage id={"Color.customInput.description"} /></div>
              : logoColor === 'multiColor' && <div className={styles.MultiColor}>
                <p><FormattedMessage id={"Color.customInput.MultiColorPDF"}/></p>
                <p><FormattedMessage id={"Color.customInput.MultiColorCost"}/></p>
            </div>
          }
          {isError &&
          <div className={styles.ErrorText}>
              <FormattedMessage id={'SchoolShop.stage4.error.logoColor'} />
          </div>
          }
        </Card>
        <Card
            title={'UnionCloth.stage4.logoFont.title'}
            description={'UnionCloth.stage4.logoFont.description'}
            value={logoFont}
            onContentOpen={() => {
              setOpenContent(4)
            }}
            hideContent={openContent !== 4}
        >
            <InputButton
                onClick={openModal}
                label={'UnionCloth.stage4.logoFont.lable'}
            >
              {logoFont ? <FormattedMessage id={`LogoFont.${logoFont}`}/> : 'Classic, Modern or College'}
            </InputButton>
        </Card>
        <Card
            title="UnionCloth.stage4.personalise.title"
            description="UnionCloth.stage4.personalise.description"
            onContentOpen={() => {
              setOpenContent(5)
            }}
            hideContent={openContent !== 5}
        >
            <div style={{ marginTop: '7px', height: '1px' }}/>
          {persons.length > 0 &&
          persons.map((item: { size: string, firstText: string, secondText: string }, index: number) => (
            <LocalCard
              key={index}
              value={item}
              allSizes={unionCloth[clothType].sizes}
              sizesString={unionCloth[clothType].sizes.join(', ')}
              onClose={() => {
                const newNewPersons = [...persons]
                newNewPersons.splice(index, 1)
                setPersons(newNewPersons)
              }}
              onEdit={(value: { size: string, firstText: string, secondText: string }) => {
                const newNewPersons = [...persons]
                // @ts-ignore
                newNewPersons[index] = value
                setPersons(newNewPersons)
              }}
            />
          ))
          }
            <div className={`${styles.SizesWrapper} ${isError && styles.Error}`}>
                <h3 className={styles.AddPerson}><FormattedMessage id={'UnionCloth.stage4.editPerson'} /></h3>
                <Select
                    values={unionCloth[clothType].sizes}
                    onChange={setSizeValue}
                    title="EventCloth.stage4.selectSize"
                    rotate
                >
                  {size ?
                    size
                    : unionCloth[clothType].sizes.join(', ')}
                </Select>
                <div className={styles.SizesInputs}>
                    <CustomInput
                        title="UnionCloth.stage4.firstText"
                        placeholder="Max 20 tecken"
                        onChange={setFirstTextValue}
                        value={firstText}
                        type="text"
                    />
                    <CustomInput
                        title="UnionCloth.stage4.secondText"
                        placeholder="Max 20 tecken"
                        onChange={setSecondTextValue}
                        value={secondText}
                        type="text"
                    />
                    <button
                        className={styles.AddSizesButton}
                        onClick={addItems}
                    >
                        <FormattedMessage id={'UnionCloth.stage4.quantity.button'}/>
                    </button>
                </div>
            </div>
          {isError &&
          <div className={styles.ErrorText}>
              <FormattedMessage id={'Union.stage4.error.persons'}/>
          </div>}
        </Card>
      </div>

      <Button
        onClick={next}
        buttonDescription={isLoading ? 'loading' : 'saveChanges'}
        buttonColor
        center
      />

      <Modal title={
        openContent === 1
          ? 'SchoolShop.stage4.modal.design.title'
          : openContent === 3
          ? 'SchoolShop.stage4.modal.logotype.title'
          : openContent === 4
            ? 'UnionCloth.stage4.modal.logoFont.title' : ''
      }>
        {openContent === 1 && designModal}
        {openContent === 4 && logoFontModal}
      </Modal>
    </ModalProvider>
  )
}
